import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { useState } from "react"
import { api } from "../../../../convex/_generated/api"
import type { Id, Doc } from "../../../../convex/_generated/dataModel"
import { inNumberRange, parseIntegerDraft } from "@/lib/numberDraft"

export const Route = createFileRoute("/_authed/admin/rewards")({
  component: AdminRewards,
})

function AdminRewards() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Rewards</h1>
        <p className="mt-1 text-sm text-[color:var(--muted-foreground)]">
          Per-venue catalog. Free-tier players see only their home-venue
          rewards; paid Wild Pass sees everything.
        </p>
      </header>
      <NewRewardForm />
      <RewardsList />
    </div>
  )
}

function NewRewardForm() {
  const venues = useQuery(api.venues.listAll)
  const create = useMutation(api.rewards.create)
  const [open, setOpen] = useState(false)
  const [venueId, setVenueId] = useState<Id<"venues"> | "">("")
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [xpCostDraft, setXpCostDraft] = useState("300")
  const [tier, setTier] = useState<"free_and_paid" | "paid_only">(
    "free_and_paid",
  )
  const [kind, setKind] = useState<
    "drink" | "seating" | "flair" | "merch" | "other"
  >("drink")
  const [submitting, setSubmitting] = useState(false)
  const xpCost = parseIntegerDraft(xpCostDraft)
  const xpCostValid = inNumberRange(xpCost, { min: 0, max: 999_999 })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!venueId || !xpCostValid || xpCost === null) return
    setSubmitting(true)
    try {
      await create({
        venueId,
        name,
        description: description || undefined,
        xpCost,
        tierEligibility: tier,
        kind,
        active: true,
      })
      setName("")
      setDescription("")
      setXpCostDraft("300")
      setOpen(false)
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="rounded-md border border-dashed border-[color:var(--border)] px-3 py-1.5 text-xs font-medium hover:bg-[color:var(--muted)]"
      >
        + New reward
      </button>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border border-[color:var(--border)] p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">New reward</h2>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-[color:var(--muted-foreground)]"
        >
          Cancel
        </button>
      </div>
      <select
        value={venueId}
        onChange={(e) => setVenueId(e.target.value as Id<"venues">)}
        required
        className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-3 py-2 text-sm"
      >
        <option value="">Select a venue…</option>
        {venues?.map((v) => (
          <option key={v._id} value={v._id}>
            {v.name}
          </option>
        ))}
      </select>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        aria-label="Reward name"
        placeholder="Reward name (e.g. Free drink)"
        required
        className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-3 py-2 text-sm"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        aria-label="Reward description"
        placeholder="Description (optional)"
        rows={2}
        className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-3 py-2 text-sm"
      />
      <div className="grid grid-cols-3 gap-2">
        <label className="text-xs">
          <span className="text-[color:var(--muted-foreground)]">Wild Points cost</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={xpCostDraft}
            onChange={(e) => setXpCostDraft(e.target.value)}
            aria-invalid={!xpCostValid || undefined}
            className="mt-1 w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs">
          <span className="text-[color:var(--muted-foreground)]">Tier</span>
          <select
            value={tier}
            onChange={(e) =>
              setTier(e.target.value as "free_and_paid" | "paid_only")
            }
            className="mt-1 w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-2 py-1 text-sm"
          >
            <option value="free_and_paid">Free + Paid</option>
            <option value="paid_only">Paid only</option>
          </select>
        </label>
        <label className="text-xs">
          <span className="text-[color:var(--muted-foreground)]">Kind</span>
          <select
            value={kind}
            onChange={(e) =>
              setKind(
                e.target.value as
                  | "drink"
                  | "seating"
                  | "flair"
                  | "merch"
                  | "other",
              )
            }
            className="mt-1 w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-2 py-1 text-sm"
          >
            <option value="drink">Drink</option>
            <option value="seating">Seating</option>
            <option value="flair">Flair</option>
            <option value="merch">Merch</option>
            <option value="other">Other</option>
          </select>
        </label>
      </div>
      <button
        type="submit"
        disabled={submitting || !venueId || !name.trim() || !xpCostValid}
        className="rounded-md bg-[color:var(--primary)] px-3 py-1.5 text-xs font-medium text-[color:var(--primary-foreground)] disabled:opacity-50"
      >
        {submitting ? "Creating…" : "Create reward"}
      </button>
    </form>
  )
}

function RewardsList() {
  const rewards = useQuery(api.rewards.listAll)
  const update = useMutation(api.rewards.update)
  if (!rewards) {
    return (
      <p className="text-sm text-[color:var(--muted-foreground)]">Loading…</p>
    )
  }
  if (rewards.length === 0) {
    return (
      <p className="text-sm text-[color:var(--muted-foreground)]">
        No rewards yet.
      </p>
    )
  }
  // Group by venue
  const byVenue = new Map<
    string,
    {
      venue: Doc<"venues"> | null
      rewards: (typeof rewards)[number][]
    }
  >()
  for (const row of rewards) {
    const key = row.venue?._id ?? "no_venue"
    if (!byVenue.has(key)) byVenue.set(key, { venue: row.venue, rewards: [] })
    byVenue.get(key)!.rewards.push(row)
  }
  return (
    <div className="space-y-4">
      {Array.from(byVenue.values()).map(({ venue, rewards: vrewards }) => (
        <div
          key={venue?._id ?? "no_venue"}
          className="rounded-md border border-[color:var(--border)] p-4"
        >
          <h3 className="font-display text-sm font-semibold">
            {venue?.name ?? "Unknown venue"}
          </h3>
          <ul className="mt-2 divide-y divide-[color:var(--border)]">
            {vrewards.map((row) => (
              <li
                key={row._id}
                className="flex items-center gap-2 py-2 text-xs"
              >
                <span className="flex-1">{row.name}</span>
                <span className="font-mono text-[10px] tabular-nums text-[color:var(--muted-foreground)]">
                  {row.xpCost} WP
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[color:var(--muted-foreground)]">
                  {row.tierEligibility === "paid_only" ? "paid only" : "any"}
                </span>
                <button
                  onClick={() =>
                    update({
                      id: row._id,
                      patch: { active: !row.active },
                    })
                  }
                  className={[
                    "rounded-md px-2 py-1 text-[10px] uppercase tracking-[0.15em]",
                    row.active
                      ? "bg-[color:var(--accent)]/30"
                      : "border border-[color:var(--border)]",
                  ].join(" ")}
                >
                  {row.active ? "active" : "paused"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
